// Format price safely - API may return string or number
// Returns formatted price WITHOUT $ prefix (add $ manually where needed)
export function formatPrice(price: unknown): string {
  const numPrice = typeof price === 'string' ? parseFloat(price) : Number(price)
  return isNaN(numPrice) ? '0.00' : numPrice.toFixed(2)
}

// Format number safely
export function formatNumber(value: unknown, decimals = 2): string {
  const num = typeof value === 'string' ? parseFloat(value) : Number(value)
  return isNaN(num) ? '0' : num.toFixed(decimals)
}

// Parse number safely
export function toNumber(value: unknown): number {
  const num = typeof value === 'string' ? parseFloat(value) : Number(value)
  return isNaN(num) ? 0 : num
}

// Format currency with $ prefix
export function formatCurrency(amount: unknown): string {
  return `$${formatPrice(amount)}`
}

// Round cash change to the nearest $0.05 — pennies aren't kept in the till,
// so the cashier physically can't hand back $0.46. Half-up at $0.025.
export function roundCashChange(amount: number): number {
  return Math.round(amount * 20) / 20
}

// Calculate tax amount
export function calculateTax(subtotal: number, taxRate: number): number {
  return subtotal * (taxRate / 100)
}

// Pull total tax out of a BE totals object. Receipts and refund responses
// return tax in two shapes: an aggregated `taxes` (or legacy `tax`) field,
// or split across `tax1_amount`..`tax4_amount` (Canada multi-jurisdiction
// receipts). Refund flows hit the latter shape — `taxes` is absent — so a
// caller that only reads `t.taxes ?? t.tax` quietly resolves to 0 and the
// cashier sees no tax line on either the cart or the CFD. Use !== 0 (not
// > 0) so refund totals (negative tax) survive the precedence check.
export function extractTaxes(totals: Record<string, unknown> | null | undefined): number {
  if (!totals) return 0
  const direct = Number(totals.taxes ?? totals.tax ?? 0)
  if (Number.isFinite(direct) && direct !== 0) return direct
  return Number(totals.tax1_amount ?? totals.tax1 ?? 0)
    + Number(totals.tax2_amount ?? totals.tax2 ?? 0)
    + Number(totals.tax3_amount ?? totals.tax3 ?? 0)
    + Number(totals.tax4_amount ?? totals.tax4 ?? 0)
}

// Truncate text with ellipsis
export function truncate(str: string, length: number): string {
  if (str.length <= length) return str
  return str.slice(0, length) + '...'
}
